/**
 * `PurgeView` — the ADR-0180 slice-2b purge-approval board.
 *
 * The nightly operational-retention cron writes ONE pending `purge_run`
 * proposal: per-table, per-account stale-row COUNTS with the cutoff
 * timestamps frozen at proposal time. This view renders the pending
 * proposal for review and offers Approve (typed-confirm — this deletes
 * rows) or Reject (with a reason). Approval executes the sweep bounded
 * by the FROZEN cutoffs, so what was approved is exactly what dies.
 * Decided runs render below as history with actual deleted counts.
 *
 * The surface carries counts only — no tenant row content.
 */

import { RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { AdminPurgeRun } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { TypedConfirmDialog } from '../../components/atoms/TypedConfirmDialog.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import { JobLastRunNote } from '../jobs/JobLastRunNote.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

function statusTone(status: AdminPurgeRun['status']): PillTone {
  switch (status) {
    case 'pending':
      return 'amber';
    case 'approved':
      return 'sky';
    case 'executed':
      return 'lime';
    case 'rejected':
      return 'coral';
    case 'failed':
      return 'coral';
    case 'superseded':
      return 'neutral';
  }
}

/** Map a decision failure to a specific, actionable message — a bare
 *  "failed" once hid a 403 (staff not superadmin) behind a generic
 *  string and cost a debugging round-trip. Surface the real cause. */
function decisionErrorMessage(err: unknown, verb: 'Approve' | 'Reject'): string {
  if (err instanceof ApiError) {
    if (err.kind === 'conflict') return 'Already decided elsewhere — refresh.';
    if (err.kind === 'forbidden') {
      return 'Your staff account is not authorized (superadmin required).';
    }
  }
  return `${verb} failed. The run is untouched; retry or check logs.`;
}

/** Group the flat (table, account, rows) summary by table for display. */
function groupByTable(
  summary: readonly AdminPurgeRun['summary'][number][],
): Map<string, { accounts: number; rows: number }> {
  const by = new Map<string, { accounts: number; rows: number }>();
  for (const r of summary) {
    const cur = by.get(r.table) ?? { accounts: 0, rows: 0 };
    by.set(r.table, { accounts: cur.accounts + 1, rows: cur.rows + r.rows });
  }
  return by;
}

export function PurgeView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [runs, setRuns] = useState<readonly AdminPurgeRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [decideError, setDecideError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [autoMode, setAutoMode] = useState<boolean | null>(null);
  const [togglePending, setTogglePending] = useState(false);
  const [autoConfirmOpen, setAutoConfirmOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [runsRes, settingsRes] = await Promise.all([
        api.listPurgeRuns(),
        api.listSettings(),
      ]);
      setRuns(runsRes.runs);
      setAutoMode(
        settingsRes.settings.find(
          (s) => s.key === 'operational_retention_auto',
        )?.bool_value ?? false,
      );
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Failed to load purge runs.',
      );
    } finally {
      setLoading(false);
    }
  }, [api]);

  const applyToggle = async (next: boolean): Promise<void> => {
    setTogglePending(true);
    try {
      const res = await api.setSetting('operational_retention_auto', next);
      setAutoMode(
        res.settings.find((s) => s.key === 'operational_retention_auto')
          ?.bool_value ?? next,
      );
      setAutoConfirmOpen(false);
    } catch (err) {
      setError(
        err instanceof ApiError && err.kind === 'forbidden'
          ? 'Superadmin required to change the mode.'
          : 'Failed to change mode — retry.',
      );
    } finally {
      setTogglePending(false);
    }
  };

  const toggleAuto = (): void => {
    if (autoMode === null || togglePending) return;
    // Switching TO automatic enables unattended deletion — confirm it.
    // Switching back to manual is safe and immediate.
    if (!autoMode) {
      setAutoConfirmOpen(true);
    } else {
      void applyToggle(false);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  const pending = runs.find((r) => r.status === 'pending') ?? null;
  const history = runs.filter((r) => r.status !== 'pending');

  const approve = async (): Promise<void> => {
    if (pending === null) return;
    setDeciding(true);
    setDecideError(null);
    try {
      await api.approvePurgeRun(pending.purge_run_id);
      setConfirmOpen(false);
      await load();
    } catch (err) {
      setDecideError(decisionErrorMessage(err, 'Approve'));
    } finally {
      setDeciding(false);
    }
  };

  const reject = async (): Promise<void> => {
    if (pending === null || rejectReason.trim().length === 0) return;
    setDeciding(true);
    setDecideError(null);
    try {
      await api.rejectPurgeRun(pending.purge_run_id, rejectReason.trim());
      setRejectReason('');
      await load();
    } catch (err) {
      setDecideError(decisionErrorMessage(err, 'Reject'));
    } finally {
      setDeciding(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              color: C.muted,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            ADR-0180 · Operational retention
          </div>
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 28,
              color: C.text,
              margin: 0,
            }}
          >
            Purge approvals
          </h1>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: sansFont,
            fontSize: 12,
            padding: '6px 12px',
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {autoMode !== null ? (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: C.text,
                }}
              >
                Purge mode: {autoMode ? 'Automatic' : 'Manual (approval required)'}
              </div>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12.5,
                  color: C.muted,
                  marginTop: 2,
                  maxWidth: '60ch',
                }}
              >
                {autoMode
                  ? 'The nightly retention tick deletes unattended. Proposals are not created.'
                  : 'The nightly tick proposes; nothing is deleted until you approve it here. Takes effect on the next tick after a change.'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggleAuto()}
              disabled={togglePending}
              style={{
                fontFamily: sansFont,
                fontSize: 12.5,
                fontWeight: 600,
                padding: '8px 14px',
                whiteSpace: 'nowrap',
                background: autoMode ? 'transparent' : C.lime,
                color: autoMode ? C.body : C.ink,
                border: `1px solid ${autoMode ? C.border : C.lime}`,
                borderRadius: 3,
                cursor: 'pointer',
                opacity: togglePending ? 0.6 : 1,
              }}
            >
              {autoMode ? 'Switch to manual' : 'Switch to automatic'}
            </button>
          </div>
        </Card>
      ) : null}

      {error !== null ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>
            {error}
          </div>
        </Card>
      ) : null}

      {loading ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          Loading…
        </div>
      ) : null}

      {!loading && pending === null && error === null ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body }}>
            Nothing pending. The retention job checks daily and proposes a
            purge when any operational rows have aged past their windows
            (notification log, sync and invite attempts, smoke runs, agent
            page hits, crawler events: 90 days; red-team results: 365 days).
            Audit records are never purged.
          </div>
          <div style={{ marginTop: 8 }}>
            <JobLastRunNote job_name="job:operational_retention" refreshKey={runs} />
          </div>
        </Card>
      ) : null}

      {pending !== null ? (
        <Card style={{ padding: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Pill tone="amber">pending</Pill>
              <span
                style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}
              >
                proposed {formatIsoCompact(pending.created_at)}
              </span>
            </div>
            <span
              style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}
            >
              {pending.total_rows.toLocaleString()} rows total
            </span>
          </div>

          <TableScroll minWidth={480}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: sansFont,
                fontSize: 13,
              }}
            >
              <thead>
                <tr>
                  {['Table', 'Cutoff (frozen)', 'Accounts', 'Rows'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Rows' || h === 'Accounts' ? 'right' : 'left',
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        color: C.muted,
                        padding: '6px 8px',
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from(groupByTable(pending.summary).entries()).map(
                  ([table, agg]) => (
                    <tr key={table}>
                      <td style={{ padding: '6px 8px', color: C.text, fontFamily: monoFont, fontSize: 12 }}>
                        {table}
                      </td>
                      <td style={{ padding: '6px 8px', color: C.muted, fontFamily: monoFont, fontSize: 11 }}>
                        {pending.cutoffs[table] !== undefined
                          ? formatIsoCompact(pending.cutoffs[table])
                          : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', color: C.body, textAlign: 'right' }}>
                        {agg.accounts}
                      </td>
                      <td style={{ padding: '6px 8px', color: C.text, textAlign: 'right' }}>
                        {agg.rows.toLocaleString()}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </TableScroll>

          {decideError !== null ? (
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12.5,
                color: C.coralFg,
                marginTop: 10,
              }}
            >
              {decideError}
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginTop: 14,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={deciding}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: sansFont,
                fontSize: 12.5,
                fontWeight: 600,
                padding: '8px 14px',
                background: C.lime,
                color: C.ink,
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
              }}
            >
              <Trash2 size={13} /> Approve purge
            </button>
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reject reason…"
              style={{
                fontFamily: sansFont,
                fontSize: 12.5,
                padding: '8px 10px',
                background: C.surface2,
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                minWidth: 220,
              }}
            />
            <button
              type="button"
              onClick={() => void reject()}
              disabled={deciding || rejectReason.trim().length === 0}
              style={{
                fontFamily: sansFont,
                fontSize: 12.5,
                padding: '8px 14px',
                background: 'transparent',
                color: C.coralFg,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                cursor: 'pointer',
                opacity: rejectReason.trim().length === 0 ? 0.5 : 1,
              }}
            >
              Reject
            </button>
          </div>
        </Card>
      ) : null}

      {history.length > 0 ? (
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              color: C.muted,
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            History
          </div>
          {history.map((r) => (
            <div
              key={r.purge_run_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 4px',
                borderBottom: `1px solid ${C.border}`,
                fontFamily: sansFont,
                fontSize: 12.5,
                color: C.body,
                flexWrap: 'wrap',
              }}
            >
              <Pill tone={statusTone(r.status)}>{r.status}</Pill>
              <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                {formatIsoCompact(r.created_at)}
              </span>
              <span>{r.total_rows.toLocaleString()} rows proposed</span>
              {r.executed_rows !== null ? (
                <span style={{ color: C.muted }}>
                  {Object.values(r.executed_rows)
                    .reduce((a, b) => a + b, 0)
                    .toLocaleString()}{' '}
                  deleted
                </span>
              ) : null}
              {r.reject_reason !== null ? (
                <span style={{ color: C.muted }}>“{r.reject_reason}”</span>
              ) : null}
              {r.decided_by !== null ? (
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
                  by {r.decided_by}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <TypedConfirmDialog
        open={confirmOpen}
        title="Approve purge"
        body={
          pending !== null ? (
            <span>
              Permanently deletes <strong>{pending.total_rows.toLocaleString()}</strong>{' '}
              operational rows across{' '}
              {groupByTable(pending.summary).size} table(s), bounded by the
              frozen cutoffs shown. Audit records are structurally excluded.
              This cannot be undone.
            </span>
          ) : undefined
        }
        expected="purge"
        inputLabel={'Type "purge" to confirm'}
        confirmLabel="Approve & execute"
        pending={deciding}
        error={decideError}
        onConfirm={() => void approve()}
        onCancel={() => {
          setConfirmOpen(false);
          setDecideError(null);
        }}
      />

      <ConfirmDialog
        open={autoConfirmOpen}
        title="Switch to automatic purging?"
        tone="coral"
        body="The nightly retention tick will DELETE aged operational rows unattended, with no approval step. You can switch back to manual at any time."
        confirmLabel="Enable automatic"
        pending={togglePending}
        onConfirm={() => void applyToggle(true)}
        onCancel={() => setAutoConfirmOpen(false)}
      />
    </div>
  );
}
